import { useEffect, useRef, useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import logo from "../assets/logo.PNG";

gsap.registerPlugin(ScrollTrigger);

const YT_ID = "1hRrR48vl9U";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: #fff; overflow-x: hidden; }
`;

// ── Keyframes
const shimmerPurple = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

// ── Page shell
const Page = styled.div`
  background: #fff;
  min-height: 100vh;
  font-family: "Mendl", sans-serif;
`;

// ── HERO ──────────────────────────────────────────────────────────────────────
const Hero = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: clamp(3rem, 8vw, 7rem) clamp(1.5rem, 6vw, 5rem)
    clamp(4rem, 8vw, 6rem);
  position: relative;
  background: #fff;
`;

const HeroTag = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #a855f7;
  margin-bottom: 2rem;
  opacity: 0;
  transform: translateY(16px);
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 1rem;

  &::before {
    content: "";
    width: 32px;
    height: 1px;
    background: #a855f7;
    display: inline-block;
  }
`;

const HeroTitle = styled.h1`
  font-family: "Mendl", sans-serif;
  font-size: clamp(3.5rem, 9vw, 9rem);
  font-weight: 700;
  line-height: 0.95;
  color: #0a0010;
  position: relative;
  z-index: 1;
  overflow: visible;

  .line-wrap {
    overflow: visible;
    display: block;
  }

  em {
    font-style: italic;
    font-weight: 400;
    background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    padding-right: 0.12em;
    animation: ${shimmerPurple} 5s linear infinite;
  }
`;

const HeroSub = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: clamp(1rem, 1.6vw, 1.2rem);
  font-weight: 300;
  color: rgba(10, 0, 16, 0.45);
  line-height: 1.8;
  max-width: 520px;
  margin-top: 2rem;
  position: relative;
  z-index: 1;
  opacity: 0;
  transform: translateY(20px);
`;

const HeroRule = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: rgba(168, 85, 247, 0.15);
`;

// ── FOUNDER SECTION ───────────────────────────────────────────────────────────
const FounderSection = styled.section`
  padding: clamp(5rem, 10vw, 9rem) clamp(1.5rem, 6vw, 5rem);
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: clamp(3rem, 6vw, 8rem);
  align-items: start;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const FounderLeft = styled.div`
  position: sticky;
  top: clamp(4rem, 8vh, 6rem);

  @media (max-width: 900px) {
    position: static;
    top: auto;
  }
`;

const FounderPhoto = styled.div`
  width: 100%;
  aspect-ratio: 16/9;
  border-radius: 12px;
  background: linear-gradient(160deg, #f3e8ff, #ede9fe, #ddd6fe);
  position: relative;
  overflow: hidden;
  margin-bottom: 1.5rem;
  opacity: 0;
  transform: translateY(40px);
`;

const FounderVideo = styled.iframe`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;

const FounderName = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 1.4rem;
  font-weight: 400;
  color: #0a0010;
  margin-bottom: 0.3rem;
  opacity: 0;
  transform: translateY(16px);
`;

const FounderRole = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #a855f7;
  opacity: 0;
  transform: translateY(12px);
`;

const FounderCredRow = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
  opacity: 0;
  transform: translateY(12px);
`;

const FounderCred = styled.div`
  text-align: center;
`;

const CredNum = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: #0a0010;
  line-height: 1;
  margin-bottom: 0.2rem;

  span {
    color: #a855f7;
  }
`;

const CredLabel = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.65rem;
  font-weight: 400;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(10, 0, 16, 0.4);
`;

// ── Right: story text
const FounderRight = styled.div`
  background: white;
`;

const StoryBlock = styled.div`
  padding-bottom: clamp(3rem, 5vw, 4.5rem);
  opacity: 1;
  transform: translateY(35px);
  background: white;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StoryLabel = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #a855f7;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  background: white;

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: white;
    max-width: 60px;
  }
`;

const StoryHeading = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: clamp(1.6rem, 2.8vw, 2.4rem);
  font-weight: 400;
  line-height: 1.25;
  color: #0a0010;
  margin-bottom: 1.2rem;

  em {
    font-style: italic;
    color: #a855f7;
  }
`;

const StoryBody = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: clamp(0.95rem, 1.3vw, 1.05rem);
  font-weight: 300;
  color: rgba(10, 0, 16, 0.55);
  line-height: 1.9;
`;

// ── SPICY SECTION ─────────────────────────────────────────────────────────────
const SpicySection = styled.section`
  padding: clamp(5rem, 10vw, 9rem) clamp(1.5rem, 6vw, 5rem);
  background: #0a0010;
  position: relative;
  overflow: visible;
`;

const SpicyBg = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.06;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
`;

const SpicyInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SpicyLabel = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #c084fc;
  margin-bottom: 2rem;
  opacity: 0;
  transform: translateY(16px);
`;

const SpicyTitle = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: clamp(2.5rem, 7vw, 6.5rem);
  font-weight: 700;
  color: #fff;
  line-height: 0.95;
  margin-bottom: clamp(3rem, 6vw, 5rem);
  opacity: 0;
  transform: translateY(30px);

  em {
    font-style: italic;
    font-weight: 400;
    background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    font-family: "Voltage";
    padding-right: 0.12em;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmerPurple} 5s linear infinite;
  }
`;

const SpicyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1px;
  background: rgba(168, 85, 247, 0.1);
  border: 1px solid rgba(168, 85, 247, 0.1);
  border-radius: 16px;
  overflow: visible;

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const SpicyCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  padding: clamp(1.5rem, 2.5vw, 2.2rem);
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  opacity: 0;
  transform: translateY(30px);
  transition: background 0.3s ease;

  &:hover {
    background: rgba(168, 85, 247, 0.07);
  }
`;

const SpicyLetter = styled.div`
  font-family: "Voltage", sans-serif;
  font-size: clamp(3rem, 5vw, 5rem);
  font-weight: 700;
  font-style: italic;
  line-height: 1;
  background: linear-gradient(135deg, #e879f9, #a855f7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  padding-bottom: 0.19em;
`;

const SpicyWord = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
`;

const SpicyDesc = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.82rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.3);
  line-height: 1.65;
`;

const SpicyNote = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: clamp(1.1rem, 2vw, 1.5rem);
  font-style: italic;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  margin-top: clamp(2.5rem, 4vw, 3.5rem);
  max-width: 700px;
  line-height: 1.6;
  opacity: 0;
  transform: translateY(20px);

  span {
    color: #c084fc;
    font-style: italic;
  }
`;

// ── QUOTE SECTION ─────────────────────────────────────────────────────────────
const QuoteSection = styled.section`
  padding: clamp(6rem, 12vw, 11rem) clamp(1.5rem, 6vw, 5rem);
  background: #fff;
  max-width: 1000px;
  margin: 0 auto;
`;

const QuoteText = styled.blockquote`
  font-family: "Mendl", sans-serif;
  font-size: clamp(1.8rem, 4vw, 3.5rem);
  font-weight: 400;
  font-style: italic;
  color: #0a0010;
  line-height: 1.3;
  margin-bottom: 2rem;
  opacity: 0;
  transform: translateY(30px);
  position: relative;
  padding-left: 2rem;
  border-left: 3px solid #a855f7;

  span {
    background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    padding-right: 0.12em;
    animation: ${shimmerPurple} 5s linear infinite;
  }
`;

const QuoteAttrib = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #a855f7;
  padding-left: 2rem;
  opacity: 0;
  transform: translateY(16px);
`;

// ── SECURITY SECTION ──────────────────────────────────────────────────────────
const SecuritySection = styled.section`
  padding: clamp(5rem, 10vw, 9rem) clamp(1.5rem, 6vw, 5rem);
  background: #faf5ff;
  position: relative;
`;

const SecurityInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(3rem, 6vw, 7rem);
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SecurityLeft = styled.div``;

const SecurityRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const SecurityHeading = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  line-height: 1.1;
  color: #0a0010;
  margin-bottom: 1.5rem;
  opacity: 0;
  transform: translateY(25px);

  em {
    font-style: italic;
    color: #a855f7;
  }
`;

const SecurityBody = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: clamp(0.9rem, 1.3vw, 1rem);
  font-weight: 300;
  color: rgba(10, 0, 16, 0.5);
  line-height: 1.9;
  opacity: 0;
  transform: translateY(20px);
`;

const SecurityCard = styled.div`
  background: #fff;
  border: 1px solid rgba(168, 85, 247, 0.12);
  border-radius: 16px;
  padding: 1.4rem 1.6rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  opacity: 0;
  transform: translateX(30px);
  transition:
    border-color 0.2s,
    box-shadow 0.2s;

  &:hover {
    border-color: rgba(168, 85, 247, 0.3);
    box-shadow: 0 4px 20px rgba(168, 85, 247, 0.08);
  }
`;

const SecurityCardIcon = styled.div`
  font-size: 1.4rem;
  flex-shrink: 0;
  margin-top: 2px;
`;

const SecurityCardText = styled.div``;

const SecurityCardTitle = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.88rem;
  font-weight: 500;
  color: #0a0010;
  margin-bottom: 0.3rem;
`;

const SecurityCardDesc = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.8rem;
  font-weight: 300;
  color: rgba(10, 0, 16, 0.45);
  line-height: 1.6;
`;

// ── CTA SECTION ───────────────────────────────────────────────────────────────
const CtaSection = styled.section`
  padding: clamp(6rem, 12vw, 11rem) clamp(1.5rem, 6vw, 5rem);
  background: #fff;
  text-align: center;
`;

const CtaHeadline = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: clamp(2.5rem, 6vw, 5.5rem);
  font-weight: 700;
  line-height: 1.05;
  color: #0a0010;
  margin-bottom: 1.5rem;
  opacity: 0;
  transform: translateY(30px);

  em {
    font-style: italic;
    font-weight: 400;
    background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmerPurple} 5s linear infinite;
  }
`;

const CtaBody = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: clamp(1rem, 1.5vw, 1.15rem);
  font-weight: 300;
  color: rgba(10, 0, 16, 0.45);
  line-height: 1.8;
  max-width: 560px;
  margin: 0 auto 2.5rem;
  opacity: 0;
  transform: translateY(20px);
`;

const CtaButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  opacity: 0;
  transform: translateY(20px);
`;

const BtnPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 2rem;
  height: 52px;
  border-radius: 100px;
  background: linear-gradient(135deg, #7c3aed, #a855f7, #c026d3);
  border: none;
  font-family: "Mendl", sans-serif;
  font-size: 0.88rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  color: #fff;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 8px 30px rgba(124, 58, 237, 0.35);
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 40px rgba(124, 58, 237, 0.45);
  }
`;

const BtnSecondary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 2rem;
  height: 52px;
  border-radius: 100px;
  background: transparent;
  border: 1px solid rgba(168, 85, 247, 0.35);
  font-family: "Mendl", sans-serif;
  font-size: 0.88rem;
  font-weight: 400;
  letter-spacing: 0.06em;
  color: #7c3aed;
  text-decoration: none;
  cursor: pointer;
  transition:
    background 0.2s,
    border-color 0.2s,
    transform 0.2s;

  &:hover {
    background: rgba(168, 85, 247, 0.05);
    border-color: rgba(168, 85, 247, 0.6);
    transform: translateY(-2px);
  }
`;

// Fake AI Summary Popup
function AISummaryPopup({ onClose }) {
  const [dots, setDots] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "1rem",
        animation: "fadeIn 0.2s ease",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "16px",
          maxWidth: "400px",
          width: "100%",
          padding: "28px 26px",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
          animation: "slideUp 0.25s ease",
          fontFamily: "'DM Sans', system-ui, sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          <span style={{ fontSize: "26px" }}>✨</span>
          <span
            style={{
              fontSize: "13px",
              fontWeight: 600,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: "#999",
            }}
          >
            AI Summary
          </span>
        </div>

        <div
          style={{
            background: "#f5f5f5",
            borderRadius: "12px",
            padding: "18px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
          >
            <span style={{ fontSize: "14px" }}>🤖</span>
            <p
              style={{
                fontSize: "14px",
                lineHeight: 1.65,
                color: "#1a1a1a",
                margin: 0,
                fontStyle: "italic",
              }}
            >
              Analyzing all page content{dots}
            </p>
          </div>
        </div>

        <div
          style={{
            borderTop: "1px solid #ebebeb",
            paddingTop: "18px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: 600,
              marginBottom: "12px",
              color: "#0a0010",
            }}
          >
            📋 About NudR — Quick Summary
          </h3>
          <ul
            style={{
              margin: 0,
              paddingLeft: "20px",
              fontSize: "13.5px",
              color: "#555",
              lineHeight: 1.7,
            }}
          >
            <li>
              Built by Launa, a cybersecurity expert with 10+ years experience
            </li>
            <li>SPICY = Sexy, Playful, Inquisitive, Cybersecure, Youthful</li>
            <li>Cybersecurity is the foundation — not an afterthought</li>
            <li>Red Flag system, disappearing photos, and blurred location</li>
            <li>
              Physical-attraction-first app with honest, clear information
            </li>
            <li>Bold, fun, and genuinely secure — download today</li>
          </ul>
        </div>

        <button
          onClick={onClose}
          style={{
            width: "100%",
            marginTop: "24px",
            padding: "12px",
            background: "#0a0010",
            color: "white",
            border: "none",
            borderRadius: "10px",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Got it
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ── Data
const SPICY = [
  {
    letter: "S",
    word: "Sexy",
    desc: "Unapologetically bold and built for physical attraction.",
  },
  {
    letter: "P",
    word: "Playful",
    desc: "Light, fun, and never takes itself too seriously.",
  },
  {
    letter: "I",
    word: "Inquisitive",
    desc: "Curious by nature — always asking what comes next.",
  },
  {
    letter: "C",
    word: "Cybersecure",
    desc: "The non-negotiable. Every feature built around your safety.",
  },
  {
    letter: "Y",
    word: "Youthful",
    desc: "Fresh energy, modern thinking, no old-world gatekeeping.",
  },
];

const SECURITY_FEATURES = [
  {
    icon: "🚩",
    title: "Red Flag System",
    desc: "Report bad behaviour with one tap. Minor flags are visible on profiles. Critical flags suspend accounts immediately.",
  },
  {
    icon: "🔐",
    title: "Built-in from day one",
    desc: "40+ years of combined cybersecurity experience baked into every layer — not added as an afterthought.",
  },
  {
    icon: "👻",
    title: "Disappearing photos",
    desc: "Photos vanish after being seen. Screenshot attempts are detected and you're notified instantly.",
  },
  {
    icon: "📍",
    title: "Blurred location",
    desc: "We show proximity, never exact coordinates. You stay in control of who knows where you are.",
  },
];

export default function AboutPage() {
  const [showSummary, setShowSummary] = useState(false);

  // Refs
  const heroTagRef = useRef(null);
  const heroTitleRef = useRef(null);
  const heroSubRef = useRef(null);

  const photoRef = useRef(null);
  const nameRef = useRef(null);
  const roleRef = useRef(null);
  const credRef = useRef(null);

  const storyRefs = useRef([]);
  const spicyLabelRef = useRef(null);
  const spicyTitleRef = useRef(null);
  const spicyCardRefs = useRef([]);
  const spicyNoteRef = useRef(null);

  const quoteRef = useRef(null);
  const quoteAttRef = useRef(null);

  const secHeadRef = useRef(null);
  const secBodyRef = useRef(null);
  const secCardRefs = useRef([]);

  const ctaHeadRef = useRef(null);
  const ctaBodyRef = useRef(null);
  const ctaBtnsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // ── Hero: page-load, no scroll trigger
      gsap
        .timeline({ delay: 0.1 })
        .to(
          heroTagRef.current,
          { opacity: 1, y: 0, duration: 0.7, ease: "power2.out" },
          0,
        )
        .from(
          ".hero-line-1",
          { y: "105%", duration: 0.9, ease: "power3.out" },
          0.15,
        )
        .from(
          ".hero-line-2",
          { y: "105%", duration: 0.9, ease: "power3.out" },
          0.25,
        )
        .from(
          ".hero-line-3",
          { y: "105%", duration: 0.9, ease: "power3.out" },
          0.35,
        )
        .to(
          heroSubRef.current,
          { opacity: 1, y: 0, duration: 0.7, ease: "power2.out" },
          0.5,
        );

      // ── Founder photo + info
      gsap
        .timeline({
          scrollTrigger: {
            trigger: photoRef.current,
            start: "top 80%",
            end: "top 45%",
            scrub: 0.7,
          },
        })
        .to(photoRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0)
        .to(nameRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.2)
        .to(roleRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.3)
        .to(credRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.4);

      // ── Story blocks
      storyRefs.current.forEach((el) => {
        if (!el) return;
        gsap
          .timeline({
            scrollTrigger: {
              trigger: el,
              start: "top 85%",
              end: "top 50%",
              scrub: 0.7,
            },
          })
          .to(el, { opacity: 1, y: 0, ease: "power2.out" });
      });

      // ── SPICY section
      gsap
        .timeline({
          scrollTrigger: {
            trigger: spicyLabelRef.current,
            start: "top 80%",
            end: "top 50%",
            scrub: 0.7,
          },
        })
        .to(spicyLabelRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0)
        .to(
          spicyTitleRef.current,
          { opacity: 1, y: 0, ease: "power2.out" },
          0.1,
        );

      spicyCardRefs.current.forEach((el) => {
        if (!el) return;
        gsap
          .timeline({
            scrollTrigger: {
              trigger: el,
              start: "top 88%",
              end: "top 60%",
              scrub: 0.6,
            },
          })
          .to(el, { opacity: 1, y: 0, ease: "power2.out" });
      });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: spicyNoteRef.current,
            start: "top 85%",
            end: "top 55%",
            scrub: 0.7,
          },
        })
        .to(spicyNoteRef.current, { opacity: 1, y: 0, ease: "power2.out" });

      // ── Quote
      gsap
        .timeline({
          scrollTrigger: {
            trigger: quoteRef.current,
            start: "top 82%",
            end: "top 48%",
            scrub: 0.7,
          },
        })
        .to(quoteRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0)
        .to(quoteAttRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.2);

      // ── Security
      gsap
        .timeline({
          scrollTrigger: {
            trigger: secHeadRef.current,
            start: "top 80%",
            end: "top 48%",
            scrub: 0.7,
          },
        })
        .to(secHeadRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0)
        .to(secBodyRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.15);

      secCardRefs.current.forEach((el) => {
        if (!el) return;
        gsap
          .timeline({
            scrollTrigger: {
              trigger: el,
              start: "top 88%",
              end: "top 58%",
              scrub: 0.6,
            },
          })
          .to(el, { opacity: 1, x: 0, ease: "power2.out" });
      });

      // ── CTA
      gsap
        .timeline({
          scrollTrigger: {
            trigger: ctaHeadRef.current,
            start: "top 82%",
            end: "top 50%",
            scrub: 0.7,
          },
        })
        .to(ctaHeadRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0)
        .to(ctaBodyRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.15)
        .to(ctaBtnsRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.28);
    });

    return () => ctx.revert();
  }, []);

  return (
    <>
      <GlobalStyle />
      <Page>
        {/* Fixed AI button - bottom left corner */}
        <button
          onClick={() => setShowSummary(true)}
          style={{
            position: "fixed",
            bottom: "24px",
            left: "24px",
            width: "52px",
            height: "52px",
            borderRadius: "50%",
            background: "#0a0010",
            border: "none",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
            transition: "transform 0.2s, background 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.background = "#2d1a4a";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.background = "#0a0010";
          }}
        >
          <span style={{ fontSize: "24px" }}>✨</span>
        </button>

        {/* AI Summary Popup */}
        {showSummary && (
          <AISummaryPopup onClose={() => setShowSummary(false)} />
        )}

        {/* ── HERO */}
        <Hero>
          <img
            src={logo}
            alt=""
            aria-hidden="true"
            style={{
              position: "absolute",
              right: "clamp(4rem, 7vw, 7rem)",
              top: "52%",
              transform: "translateY(-50%)",
              width: "clamp(820px, 84vw, 1180px)",
              height: "auto",
              opacity: 0.07,
              pointerEvents: "none",
              userSelect: "none",
              zIndex: 0,
              filter: "grayscale(1)",
            }}
          />
          <HeroTag ref={heroTagRef}>Our Story</HeroTag>
          <HeroTitle ref={heroTitleRef}>
            <span className="line-wrap">
              <span className="hero-line-1">Bold, open,</span>
            </span>
            <span className="line-wrap">
              <span className="hero-line-2">
                and <em>safe</em>
              </span>
            </span>
            <span className="line-wrap">
              <span className="hero-line-3">from the start.</span>
            </span>
          </HeroTitle>
          <HeroSub ref={heroSubRef}>
            NudR was built by a woman who spent a decade in cybersecurity and
            knew exactly what was missing from every dating app she'd ever seen.
          </HeroSub>
          <HeroRule />
        </Hero>

        {/* ── FOUNDER */}
        <FounderSection>
          <FounderLeft>
            <FounderPhoto ref={photoRef}>
              <FounderVideo
                src={`https://www.youtube.com/embed/${YT_ID}?rel=0`}
                title="NudR — Launa's story"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </FounderPhoto>
            <FounderName ref={nameRef}>Launa R.</FounderName>
            <FounderRole ref={roleRef}>Founder & CEO · NudR</FounderRole>
            <FounderCredRow ref={credRef}>
              <FounderCred>
                <CredNum>
                  10<span>+</span>
                </CredNum>
                <CredLabel>
                  Years cyber-
                  <br />
                  security
                </CredLabel>
              </FounderCred>
              <FounderCred>
                <CredNum>
                  20<span>+</span>
                </CredNum>
                <CredLabel>
                  Years in
                  <br />
                  tech
                </CredLabel>
              </FounderCred>
              <FounderCred>
                <CredNum>
                  40<span>+</span>
                </CredNum>
                <CredLabel>
                  Team yrs
                  <br />
                  combined
                </CredLabel>
              </FounderCred>
            </FounderCredRow>
          </FounderLeft>

          <FounderRight>
            {[
              {
                label: "The beginning",
                heading: (
                  <>
                    I built what I <em>wished</em> existed.
                  </>
                ),
                body: "Hi, I'm Launa, founder of NudR. I've spent about a decade in cybersecurity and 20 years in the tech world. As a woman, I know how vulnerable we can feel online — and I got tired of seeing that ignored. So I built NudR: a dating app that's not only fun and open but safe from the very first tap.",
              },
              {
                label: "The mission",
                heading: (
                  <>
                    Fun, open, and <em>never compromised.</em>
                  </>
                ),
                body: "We built NudR so you can explore freely and never have to worry about your data or your peace of mind. Whether you're swiping, matching, sharing photos or messaging — you're protected. Not as a feature. Not as a promise. As the foundation everything else is built on.",
              },
              {
                label: "The conviction",
                heading: (
                  <>
                    As a woman, I <em>know this matters.</em>
                  </>
                ),
                body: "I know what it's like to second-guess whether a space is actually safe. That uncertainty shouldn't exist when you're trying to have fun and make real connections. NudR gives you clarity and confidence to enjoy every interaction — because empowerment isn't a buzzword here, it's the whole point.",
              },
              {
                label: "The invitation",
                heading: (
                  <>
                    This is <em>your space.</em>
                  </>
                ),
                body: "I'm inviting you to download NudR, create your account, and step into a different kind of dating experience. We're here, always ready and always listening. Feel confident, empowered, sexy, and excited as you explore. Let's make modern dating bold, fun, and genuinely secure.",
              },
            ].map((block, i) => (
              <StoryBlock key={i} ref={(el) => (storyRefs.current[i] = el)}>
                <StoryLabel>{block.label}</StoryLabel>
                <StoryHeading>{block.heading}</StoryHeading>
                <StoryBody>{block.body}</StoryBody>
              </StoryBlock>
            ))}
          </FounderRight>
        </FounderSection>

        {/* ── SPICY */}
        <SpicySection>
          <SpicyBg />
          <SpicyInner>
            <SpicyLabel ref={spicyLabelRef}>Our core pillars</SpicyLabel>
            <SpicyTitle ref={spicyTitleRef}>
              We ask one question:
              <br />
              is this <em>SPICY</em> enough?
            </SpicyTitle>
            <SpicyGrid>
              {SPICY.map((item, i) => (
                <SpicyCard
                  key={i}
                  ref={(el) => (spicyCardRefs.current[i] = el)}
                >
                  <SpicyLetter>{item.letter}</SpicyLetter>
                  <SpicyWord>{item.word}</SpicyWord>
                  <SpicyDesc>{item.desc}</SpicyDesc>
                </SpicyCard>
              ))}
            </SpicyGrid>
            <SpicyNote ref={spicyNoteRef}>
              "When we ask if something is SPICY enough, we never — ever —
              compromise on <span>C</span>. Cybersecurity sits at the heart of
              every single decision we make."
            </SpicyNote>
          </SpicyInner>
        </SpicySection>

        {/* ── QUOTE */}
        <QuoteSection>
          <QuoteText ref={quoteRef}>
            "NudR is a <span>physical-attraction-first</span> app. We know you
            make fast decisions — so we make sure you have the clearest, most
            honest information from the very first glance."
          </QuoteText>
          <QuoteAttrib ref={quoteAttRef}>— Launa R. Founder & CEO</QuoteAttrib>
        </QuoteSection>

        {/* ── SECURITY */}
        <SecuritySection>
          <SecurityInner>
            <SecurityLeft>
              <SecurityHeading ref={secHeadRef}>
                We don't <em>react</em> to risk.
                <br />
                We anticipate it.
              </SecurityHeading>
              <SecurityBody ref={secBodyRef}>
                Unlike other dating apps, we built NudR with cybersecurity from
                the ground up. Our team brings over 40 years of combined
                experience — and we didn't stop at just technology. Every
                feature, from the Red Flag system to disappearing photos, was
                designed with one question: does this keep our users safer?
              </SecurityBody>
            </SecurityLeft>
            <SecurityRight>
              {SECURITY_FEATURES.map((f, i) => (
                <SecurityCard
                  key={i}
                  ref={(el) => (secCardRefs.current[i] = el)}
                >
                  <SecurityCardIcon>{f.icon}</SecurityCardIcon>
                  <SecurityCardText>
                    <SecurityCardTitle>{f.title}</SecurityCardTitle>
                    <SecurityCardDesc>{f.desc}</SecurityCardDesc>
                  </SecurityCardText>
                </SecurityCard>
              ))}
            </SecurityRight>
          </SecurityInner>
        </SecuritySection>

        {/* ── CTA */}
        <CtaSection>
          <CtaHeadline ref={ctaHeadRef}>
            Bold, fun,
            <br />
            and <em>genuinely secure.</em>
          </CtaHeadline>
          <CtaBody ref={ctaBodyRef}>
            Ready to step into a dating experience that respects you as much as
            it excites you?
          </CtaBody>
          <CtaButtons ref={ctaBtnsRef}>
            <BtnPrimary href="#">Download NudR →</BtnPrimary>
            <BtnSecondary href="/#contact">Get in touch</BtnSecondary>
          </CtaButtons>
        </CtaSection>
      </Page>
    </>
  );
}
